import { createEffect, createSignal, Show, useContext } from 'solid-js';
import { DOMElement } from 'solid-js/jsx-runtime';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../tools/error-catcher';
import { Heading } from '../../ui-components/heading/heading';
import { Text } from '../../ui-components/text/text';
import { DocumentTypeFlag } from '../document-type-flag/document-type-flag';
import { AtosLogoRelative } from '../icons-library/atos-logo-relative';
import { CertificateIcon } from '../icons-library/certificate-icon';
import { ImageSrcData } from '../image-component/image-component';
import IntersectionObserver from '../intersection-observer/intersection-observer';
import { StyledCompletedOverlay, StyledCertificateIcon, StyledCourseCardImagePlaceholder, StyledCourseCardImage, StyledCourseCardText, StyledProgressBarOverlay, StyledCourseCard, StyledCourseCardTop, StyledCourseCardBottom } from './course-card.styles';
import { ProgressBar } from './progress-bar';
import { showpadImageProxyUrl } from '../../tools/showpad-image-proxy-url';
import { CourseTag } from '../course/course-types';

export type CourseCardData = {
    courseId: string;
    name: string;
    description: string;
    progress: number;
    completedDate: string;
    coverImageUrl: {
        small: string;
    };
    path?: {
        id: string;
        name: string;
    };
    curriculum?: {
        id: string;
        name: string;
    };
    durationInSeconds: number;
    tags?: CourseTag[]
};

type CourseCardProps = {
    course: CourseCardData;
};

export const CourseCard = (props: CourseCardProps) => {
    const {
        localize,
        coursesEntityName,
    } = useContext(AppContext);

    const [ backgroundImageSrcData, setBackgroundImageSrcData ] = createSignal<ImageSrcData>();
    const [ lowResBackgroundImagePath, setLowResBackgroundImagePath ] = createSignal<string>();

    const inProgress = () => {        
        return Number.isInteger(props.course.progress) && props.course.progress !== 0 && props.course.progress !== -1;
    };

    const setImage = () => {
        if (!props?.course.coverImageUrl.small) return;

        setLowResBackgroundImagePath(props.course.coverImageUrl.small);
    };

    createEffect(setImage);

    // We set the image both on createEffect for the frtonend and call it directly so that the SSR also sets the image
    setImage();

    const showImage = (visible: boolean | DOMElement) => {
        if (!visible || !props?.course.coverImageUrl.small) {
            return;
        }

        const url = showpadImageProxyUrl(props.course.coverImageUrl.small);

        setBackgroundImageSrcData({
            original: url,
            retina: url,
            jwt: '',
        });
    };

    const CourseCompletedOverlay = () => (
        <Show when={props.course.completedDate}>
            <StyledCompletedOverlay>
                <StyledCertificateIcon>
                    <CertificateIcon />
                </StyledCertificateIcon>
            </StyledCompletedOverlay>
        </Show>
    );

    const CourseImage = () => (
        <Show
            when={props.course.coverImageUrl.small}
            fallback={
                <StyledCourseCardImagePlaceholder>
                    <AtosLogoRelative />
                </StyledCourseCardImagePlaceholder>
            }
        >
            <StyledCourseCardImage
                src={lowResBackgroundImagePath()}
                alt=""
                role="presentation"
                animate={!!backgroundImageSrcData()}
                lowRes={true}
            />
            <StyledCourseCardImage
                src={backgroundImageSrcData()?.original || lowResBackgroundImagePath()}
                alt={localize('e-learning-course-cover-image', 'E-Learning Course Cover Image')}
            />
        </Show>
    );

    const BottomText = () => (
        <StyledCourseCardText inProgress={inProgress()}>
            <Show when={props.course.curriculum}>
                <Heading tag="h3" variant="smallPDF" lineLimit={1} color={{ name: 'gray', color: 'gray' }}>
                    {`${props.course.curriculum!.name}`}
                </Heading>
            </Show>

            <Heading tag="h3" variant="tinyGray" lineLimit={2}>
                {props.course.name}
            </Heading>

            <Text fontSize='small' color='gray' lineLimit={3} noBlockSpacing>
                {props.course.description}
            </Text>
        </StyledCourseCardText>
    );

    return (
        <ErrorCatcher componentName='CourseCard'>
            <IntersectionObserver onVisible={showImage} threshold={0.1} style={{ 'z-index': 0 }}>
                <StyledCourseCard href={`/${coursesEntityName}/${props.course.courseId}`}>

                    <Show when={props.course.durationInSeconds !== 0}>
                        <DocumentTypeFlag
                            size="small"
                            type={'course'}
                            customText={`${props.course.durationInSeconds! / 60} ${localize('min.', 'min.')}`}
                        />
                    </Show>

                    <StyledCourseCardTop>
                        <CourseCompletedOverlay />
                        <CourseImage />
                    </StyledCourseCardTop>

                    <StyledCourseCardBottom>
                        <BottomText />

                        <Show when={inProgress()}>
                            <StyledProgressBarOverlay>
                                <ProgressBar
                                    inProgress={inProgress()}
                                    progress={props.course.progress}
                                />
                            </StyledProgressBarOverlay>
                        </Show>
                    </StyledCourseCardBottom>

                </StyledCourseCard>
            </IntersectionObserver>
        </ErrorCatcher>
    );
};

